import { useEffect, useRef, useState, type FormEvent, type ReactElement, type ReactNode, useId } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorBox, useApi } from './kit';

// The campaign board (as Bridge's study page): the cards are the work. The assistant on the right fills them in and
// highlights the one it is on; the person can also edit a card in place (as Bridge's "Paste your list, or type one"),
// and that edit is noted in the thread so the assistant and the team see it. Each card keeps a short history.
// Setup mode: the cards to fill before a campaign can send. Manage mode: a started campaign's numbers and replies.

export type CardKey = 'company' | 'markets' | 'icp' | 'accounts' | 'sending' | 'messages' | 'launch' | 'review' | 'performance' | 'replies' | 'health' | 'audience';
type History = { at: string; text: string; by: string; undone: boolean };
export type Card = {
  key: CardKey; title: string; status: 'todo' | 'working' | 'done' | 'blocked'; progress?: number; eta?: string; working_label?: string;
  summary: string; items: string[]; link?: string; history?: History[]; data?: Record<string, unknown>;
};
export type Board = {
  mode: 'setup' | 'manage';
  campaign: { id: string; name: string; status: string; steps: number } | null;
  cards: Card[]; next: CardKey | null; working: Array<{ key: CardKey; label: string; progress: number; eta: string }>;
  days?: Array<{ day: string; sent: number; replied: number }>;
};
type Step = { seq_number: number; delay_in_days: number; subject: string; body: string };
type Segment = { segment: string; buyer_roles?: string[] } & Record<string, unknown>;

export const boardPath = (campaignId?: string | null) => `/portal/board${campaignId ? `?campaign=${campaignId}` : ''}`;
/** The board for the page the person is on (shared by the page and the assistant's progress widget). */
export function useBoard(campaignId?: string | null, enabled = true) {
  const [fast, setFast] = useState(false);
  const q = useApi<Board>(enabled ? boardPath(campaignId) : null, { refetchMs: fast ? 3000 : 20000 });
  useEffect(() => { setFast(Boolean(q.data?.working?.length)); }, [q.data?.working?.length]);
  return q;
}

/** Start a new chat (its own thread, titled by the question) and ask it: Home's input and chips, Inbox, Workspace. */
export type ChatKind = 'first_campaign' | 'prospect' | 'contacts' | 'import' | 'launch' | 'replies' | 'waiting' | 'analysis' | 'week' | 'developer' | 'general';
/** kind: the brief (cards to fill) the chat starts with; without it the assistant picks one */
export const newChat = (nav: (to: string) => void, text: string, kind?: ChatKind) => {
  const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(16).padStart(12, '0')}-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`;
  nav(`/chat/${id}`);
  setTimeout(() => window.dispatchEvent(new CustomEvent('olv-assistant-ask', { detail: { text, kind } })), 50);
};

/**
 * The cards' motion: while the assistant answers, its card shimmers; a card that just became done flashes once,
 * and the highlight moves on to the next.
 */
export function useCardMotion(cards: Array<{ key: string; status: string }>) {
  const [busy, setBusy] = useState(false);
  const [justDone, setJustDone] = useState<string[]>([]);
  const prev = useRef<Map<string, string> | null>(null);
  useEffect(() => {
    const f = (e: Event) => setBusy(Boolean((e as CustomEvent<boolean>).detail));
    window.addEventListener('olv-assistant-busy', f);
    return () => window.removeEventListener('olv-assistant-busy', f);
  }, []);
  const sig = cards.map((c) => `${c.key}:${c.status}`).join(',');
  useEffect(() => {
    const before = prev.current;
    prev.current = new Map(cards.map((c) => [c.key, c.status]));
    if (!before) return; // the first look is not a change
    const done = cards.filter((c) => c.status === 'done' && before.get(c.key) && before.get(c.key) !== 'done').map((c) => c.key);
    if (!done.length) return;
    setJustDone(done);
    const t = setTimeout(() => setJustDone([]), 1600);
    return () => clearTimeout(t);
  }, [sig]);
  return { busy, justDone };
}

/** Ask the assistant from the page (it opens if hidden). */
export const askAssistant = (text: string) => window.dispatchEvent(new CustomEvent('olv-assistant-ask', { detail: text }));
/** Record an edit made on the page in the thread (the assistant reads it as the new truth). */
// an edit on a card shows in the chat at once (not at the next poll); if it can't be noted there, the edit still stands
const note = (text: string, card: CardKey, campaign?: string | null) =>
  api('/portal/assistant/note', { method: 'POST', body: { text: text.slice(0, 600), card, ...(campaign ? { campaign } : {}) } })
    .then(() => window.dispatchEvent(new Event('olv-thread-changed')), () => undefined);

const I = (d: string) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
const ICONS: Record<CardKey, ReactElement> = {
  company: I('M3 21h18M5 21V7l7-4 7 4v14M9 9h1M14 9h1M9 13h1M14 13h1M10 21v-4h4v4'),
  markets: I('M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18'),
  icp: I('M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 12h.01'),
  accounts: I('M4 21V5a2 2 0 0 1 2-2h8l6 6v12M14 3v6h6M8 13h8M8 17h5'),
  sending: I('M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z'),
  messages: I('M4 4h16v12H5.2L4 17.2V4zM8 9h8M8 12h5'),
  launch: I('M9 11l3 3 8-8M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9'),
  review: I('M4 6h16v12H4zM4 7l8 6 8-6M9 16l2 2 4-4'),
  performance: I('M3 3v18h18M7 15l4-4 3 3 5-6'),
  replies: I('M9 17H5l-2 3V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-6'),
  health: I('M3 12h4l3-8 4 16 3-8h4'),
  audience: I('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8'),
};

export function StatusPill({ c }: { c: Card }) {
  if (c.status === 'done') return <span className="bd-pill done">✓ Done</span>;
  if (c.status === 'working') return <span className="bd-pill working" title={c.working_label}><span className="spin" aria-hidden="true" />{c.eta ?? 'Working'}</span>;
  if (c.status === 'blocked') return <span className="bd-pill blocked">Needs you</span>;
  return <span className="bd-pill">To do</span>;
}

const EDITABLE: Partial<Record<CardKey, true>> = { company: true, markets: true, icp: true, accounts: true, sending: true, messages: true };
const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

/** The cards. `extra` renders inside a card when it is open (the campaign page puts its checklist and emails there). */
export function BoardCards({ board, extra }: { board: Board; extra?: Partial<Record<CardKey, ReactNode>> }) {
  const [focus, setFocus] = useState<CardKey | null>(null);
  // a card clicked open spans the row; the card the chat is on lights up in its place (the order never changes)
  const [clicked, setClicked] = useState<CardKey | null>(null);
  const [editing, setEditing] = useState<CardKey | null>(null);
  const [showHistory, setShowHistory] = useState<CardKey | null>(null);
  useEffect(() => {
    const f = (e: Event) => setFocus((e as CustomEvent<CardKey>).detail);
    window.addEventListener('olv-board-focus', f);
    return () => window.removeEventListener('olv-board-focus', f);
  }, []);
  // the assistant's card, else the one being edited, else the next one to do
  const active = editing ?? (focus && board.cards.some((c) => c.key === focus) ? focus : board.next);
  useEffect(() => { if (focus) document.getElementById(`card-${focus}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [focus]);
  const campaignId = board.campaign?.id ?? null;
  const motion = useCardMotion(board.cards);
  return (
    <div className={`board ${board.mode}`}>
      {board.cards.map((c) => {
        const open = c.key === active;
        // a started campaign's chart stays in view; the rest opens with its card
        const wide = clicked === c.key || editing === c.key || (board.mode === 'manage' && c.key === 'performance');
        const isEditing = editing === c.key;
        return (
          <section key={c.key} id={`card-${c.key}`} className={`bd-card s-${c.status}${open ? ' focus' : ''}${open && motion.busy ? ' thinking' : ''}${motion.justDone.includes(c.key) ? ' just-done' : ''}${wide ? ' wide' : ''}`} aria-current={open ? 'step' : undefined} aria-labelledby={`card-${c.key}-t`}>
            {open ? <span className="bd-now"><i aria-hidden="true" />The assistant is here</span> : null}
            <button type="button" className="bd-head" aria-expanded={wide} onClick={() => setClicked(clicked === c.key ? null : c.key)}>
              <span className="bd-icon">{ICONS[c.key]}</span>
              <b id={`card-${c.key}-t`}>{c.title}</b>
              <StatusPill c={c} />
            </button>
            {c.status === 'working' && c.working_label ? <p className="bd-working">{c.working_label}…</p> : null}
            {c.status === 'working' ? <div className="bd-bar" role="progressbar" aria-label={`Estimated progress: ${c.eta ?? ''}`} aria-valuenow={c.progress ?? 5} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${c.progress ?? 5}%` }} /></div> : null}
            <p className={c.status === 'todo' && !c.items.length ? 'bd-sum muted' : 'bd-sum'}>{c.summary}</p>
            {isEditing ? <CardEditor card={c} campaignId={campaignId} onClose={() => setEditing(null)} />
              : c.items.length ? <ul className="bd-items">{(open ? c.items : c.items.slice(0, 2)).map((x, i) => <li key={i}>{x}</li>)}{!open && Math.max(c.items.length, Number(c.data?.total ?? 0)) > 2 ? <li className="muted">+{Math.max(c.items.length, Number(c.data?.total ?? 0)) - 2} more</li> : null}</ul> : null}
            {(wide || open) && !isEditing && extra?.[c.key] ? <div className="bd-extra">{extra[c.key]}</div> : null}
            {showHistory === c.key && c.history?.length ? (
              <ol className="bd-history" aria-label={`${c.title}: recent changes`}>
                {c.history.map((h, i) => <li key={i} className={h.undone ? 'undone' : ''}><span>{h.text}</span><small>{h.by} · {ago(h.at)}{h.undone ? ' · undone' : ''}</small></li>)}
              </ol>
            ) : null}
            <footer>
              <span className="bd-actions">
                {EDITABLE[c.key] && !isEditing ? <button type="button" className="link" onClick={() => setEditing(c.key)}>Edit</button> : null}
                {c.status !== 'done' && !isEditing ? <button type="button" className="link" onClick={() => askAssistant(c.status === 'blocked' ? `What is needed for ${c.title}?` : `Let's do ${c.title}`)}>{c.status === 'blocked' ? 'What is needed?' : 'Ask the assistant'}</button>
                  : board.mode === 'manage' ? <button type="button" className="link" onClick={() => askAssistant(`Analyse the ${c.title.toLowerCase()} of this campaign`)}>Analyse</button> : null}
                {c.history?.length ? <button type="button" className="link" aria-expanded={showHistory === c.key} onClick={() => setShowHistory(showHistory === c.key ? null : c.key)}>History</button> : null}
              </span>
              {c.link ? <Link className="link" to={c.link}>Open</Link> : null}
            </footer>
          </section>
        );
      })}
    </div>
  );
}

/** Edit a card in place; every change is noted in the thread. */
function CardEditor({ card, campaignId, onClose }: { card: Card; campaignId: string | null; onClose: () => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const qc = useQueryClient();
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const run = async (work: () => Promise<string | null>) => {
    setErr(null); setBusy(true);
    try {
      const said = await work();
      if (said) await note(said, card.key, campaignId);
      await qc.invalidateQueries();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const d = card.data ?? {};
  let body: ReactNode = null;

  if (card.key === 'company') {
    const facts = (d.facts as Array<{ id: string; text: string }> | undefined) ?? [];
    body = (
      <>
        <ul className="bd-edit-list">{facts.map((f) => (
          <li key={f.id}><span>{f.text}</span>
            <button type="button" className="icon-btn" aria-label={`Remove: ${f.text}`} disabled={busy}
              onClick={() => run(async () => { await api(`/portal/context/statements/${f.id}/retire`, { method: 'POST' }); return `Removed a fact: “${f.text.slice(0, 200)}”`; })}>×</button></li>
        ))}</ul>
        <form className="bd-add" onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault(); const f = new FormData(e.currentTarget); const text = String(f.get('text')).trim(); const form = e.currentTarget;
          if (text) void run(async () => { await api('/portal/context/statements', { method: 'POST', body: { topic: String(f.get('topic')), text } }); form.reset(); return `Added a fact (${String(f.get('topic'))}): “${text.slice(0, 200)}”`; });
        }}>
          <select id={`${uid}-f1`} name="topic" aria-label="Kind of fact" defaultValue="product">
            {[['product', 'What we sell'], ['customer', 'Who buys'], ['pricing', 'Pricing'], ['proof', 'Proof'], ['positioning', 'Positioning'], ['goal', 'Goal']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input id={`${uid}-f2`} name="text" placeholder="Add a fact, in your words…" maxLength={600} aria-label="New fact" />
          <button type="submit" className="btn btn-primary" disabled={busy}>Add</button>
        </form>
      </>
    );
  } else if (card.key === 'markets') {
    body = <MarketsEditor codes={(d.codes as string[] | undefined) ?? []} busy={busy} save={(next, prev) => run(async () => {
      await api('/portal/site-setup/markets', { method: 'POST', body: { countries: next } });
      return `Markets changed from ${prev.join(', ') || 'none'} to ${next.join(', ')}`;
    })} />;
  } else if (card.key === 'icp') {
    body = d.editable && d.version_id
      ? <SegmentsEditor segments={(d.segments as Segment[] | undefined) ?? []} busy={busy} save={(next, said) => run(async () => {
          await api(`/v1/icp-versions/${String(d.version_id)}`, { method: 'PATCH', body: { hypotheses: next } }); return said;
        })} />
      : <p className="muted small">These segments are submitted or approved, so they are locked. Open a new draft on the <Link to="/icp">ICP page</Link> to change them.</p>;
  } else if (card.key === 'accounts') {
    body = (
      <form className="bd-add" onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault(); const form = e.currentTarget; const domain = String(new FormData(form).get('domain')).trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
        if (domain) void run(async () => { await api('/v1/companies', { method: 'POST', body: { domain } }); form.reset(); return `Added an account: ${domain}`; });
      }}>
        <input id={`${uid}-f3`} name="domain" placeholder="company.com" aria-label="Company website" autoComplete="off" />
        <button type="submit" className="btn btn-primary" disabled={busy}>Add account</button>
        <Link className="link" to="/import">Import a list</Link>
      </form>
    );
  } else if (card.key === 'sending') {
    body = d.connected
      ? <p className="muted small">Smartlead is connected. Mailboxes and their daily caps are managed in Smartlead; <Link to="/integrations">see them here</Link>.</p>
      : (
        // the key goes straight to the API (stored encrypted), never into the conversation
        <form className="bd-add" onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault(); const form = e.currentTarget; const key = String(new FormData(form).get('api_key'));
          void run(async () => { await api('/portal/outbound/connect', { method: 'POST', body: { api_key: key } }); form.reset(); return 'Connected Smartlead'; });
        }}>
          <input id={`${uid}-f4`} name="api_key" type="password" required minLength={10} autoComplete="off" placeholder="Smartlead API key" aria-label="Smartlead API key" />
          <button type="submit" className="btn btn-primary" disabled={busy}>Connect</button>
          <span className="muted small">Stored encrypted; never shown again, never sent to the assistant.</span>
        </form>
      );
  } else if (card.key === 'messages') {
    body = d.editable && d.campaign_id
      ? <StepsEditor steps={(d.steps as Step[] | undefined) ?? []} busy={busy} save={(next, said) => run(async () => {
          await api(`/v1/campaigns/${String(d.campaign_id)}/sequence`, { method: 'PUT', body: { steps: next } }); return said;
        })} />
      : <p className="muted small">{d.campaign_id ? 'The emails of a built or started campaign cannot change. Create a new campaign to try different ones.' : 'Name the campaign first; the assistant drafts the emails, then you can edit them here.'}</p>;
  }

  return (
    <div className="bd-editor">
      {body}
      {err ? <ErrorBox error={err} /> : null}
      <div className="bd-editor-foot"><button type="button" className="link" onClick={onClose}>Done</button></div>
    </div>
  );
}

function MarketsEditor({ codes, busy, save }: { codes: string[]; busy: boolean; save: (next: string[], prev: string[]) => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [list, setList] = useState(codes);
  const [add, setAdd] = useState('');
  const changed = list.join() !== codes.join();
  return (
    <div className="bd-markets">
      <div className="chips">{list.map((c) => <span key={c} className="chip">{c}<button type="button" aria-label={`Remove ${c}`} onClick={() => setList(list.filter((x) => x !== c))}>×</button></span>)}</div>
      <form className="bd-add" onSubmit={(e) => { e.preventDefault(); const c = add.trim().toUpperCase(); if (/^[A-Z]{2}$/.test(c) && !list.includes(c)) setList([...list, c]); setAdd(''); }}>
        <input id={`${uid}-f5`} value={add} onChange={(e) => setAdd(e.target.value)} maxLength={2} placeholder="US" aria-label="Country code (2 letters)" />
        <button type="submit" className="btn">Add</button>
        <button type="button" className="btn btn-primary" disabled={busy || !changed || !list.length} onClick={() => save(list, codes)}>Save markets</button>
      </form>
      <p className="muted small">Two-letter country codes (US, CA, GB). Only the countries you sell into.</p>
    </div>
  );
}

function SegmentsEditor({ segments, busy, save }: { segments: Segment[]; busy: boolean; save: (next: Segment[], said: string) => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [list, setList] = useState(segments.map((s) => ({ ...s })));
  const [add, setAdd] = useState('');
  const said = () => {
    const before = segments.map((s) => s.segment); const after = list.map((s) => s.segment);
    const removed = before.filter((x) => !after.includes(x)); const added = after.filter((x) => !before.includes(x));
    return [added.length ? `added ${added.map((x) => `“${x}”`).join(', ')}` : '', removed.length ? `removed ${removed.map((x) => `“${x}”`).join(', ')}` : ''].filter(Boolean).join('; ') || 'renamed segments';
  };
  return (
    <div>
      <ul className="bd-edit-list">{list.map((s, i) => (
        <li key={i}>
          <input id={`${uid}-segment-${i}`} value={s.segment} aria-label={`Segment ${i + 1}`} onChange={(e) => setList(list.map((x, j) => (j === i ? { ...x, segment: e.target.value } : x)))} />
          <button type="button" className="icon-btn" aria-label={`Remove ${s.segment}`} onClick={() => setList(list.filter((_, j) => j !== i))}>×</button>
        </li>
      ))}</ul>
      <form className="bd-add" onSubmit={(e) => { e.preventDefault(); if (add.trim()) setList([...list, { segment: add.trim(), buyer_roles: [] }]); setAdd(''); }}>
        <input id={`${uid}-f7`} value={add} onChange={(e) => setAdd(e.target.value)} placeholder="Add a segment…" maxLength={200} aria-label="New segment" />
        <button type="submit" className="btn">Add</button>
        <button type="button" className="btn btn-primary" disabled={busy || JSON.stringify(list) === JSON.stringify(segments)} onClick={() => save(list.filter((s) => s.segment.trim()), `Edited the segments: ${said()}`)}>Save segments</button>
      </form>
    </div>
  );
}

function StepsEditor({ steps, busy, save }: { steps: Step[]; busy: boolean; save: (next: Step[], said: string) => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [list, setList] = useState(steps.map((s) => ({ ...s })));
  const set = (i: number, k: 'subject' | 'body', v: string) => setList(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const changes = list.flatMap((s, i) => [s.subject !== steps[i]?.subject ? `email ${i + 1} subject` : '', s.body !== steps[i]?.body ? `email ${i + 1} text` : ''].filter(Boolean));
  return (
    <div className="bd-steps">
      {list.map((s, i) => (
        <fieldset key={s.seq_number}>
          <legend>Email {i + 1} · day {s.delay_in_days}</legend>
          <input id={`${uid}-subject-${i}`} value={s.subject} maxLength={120} aria-label={`Email ${i + 1} subject`} onChange={(e) => set(i, 'subject', e.target.value)} />
          <textarea id={`${uid}-body-${i}`} value={s.body} rows={5} maxLength={3000} aria-label={`Email ${i + 1} text`} onChange={(e) => set(i, 'body', e.target.value)} />
        </fieldset>
      ))}
      <button type="button" className="btn btn-primary" disabled={busy || !changes.length} onClick={() => save(list, `Edited the emails: ${changes.join(', ')}`)}>Save emails</button>
      <p className="muted small">Saved emails go back to Outlevo for its check before they can send.</p>
    </div>
  );
}

/** Bars of the last days (Manage → Performance). */
export function DayBars({ days }: { days: Array<{ day: string; sent: number; replied: number }> }) {
  const max = Math.max(1, ...days.map((d) => d.sent));
  return (
    <div className="daybars" role="img" aria-label={`Reached per day, last ${days.length} days`}>
      {days.map((d) => <span key={d.day} title={`${d.day}: ${d.sent} reached, ${d.replied} replied`}><i style={{ height: `${(d.sent / max) * 100}%` }} />{d.replied ? <em style={{ height: `${(d.replied / max) * 100}%` }} /> : null}</span>)}
    </div>
  );
}
